/*
 * Left-rail marks and the open panel's status lines (design board "Rail numbers", option B).
 * Pure functions only, so the rules are testable without stores.
 *
 * counts: { inboxUnread, signalsNew, signalsUnread, tasksToday, tasksLate, agentsStopped, agentsNeedYou }
 * Any count may be null/undefined (unknown); unknown and zero both mean "say nothing".
 */
const n = v => (typeof v === "number" && v > 0 ? v : 0);

// The mark on the collapsed rail: red = a person waiting on you, blue = new to look at.
export const getRailMark = (name = "", counts = {}) => {
  const c = counts || {};
  if (name === "inbox" && n(c.inboxUnread)) {
    return { kind: "count", tone: "red", value: c.inboxUnread > 99 ? "99+" : String(c.inboxUnread) };
  }
  if (name === "signals" && n(c.signalsNew)) return { kind: "dot", tone: "blue" };
  if (name === "tasks" && n(c.tasksLate)) return { kind: "dot", tone: "red" };
  if (name === "agents" && n(c.agentsStopped)) return { kind: "dot", tone: "red" };
  return null;
};

// The one-line status under each label in the open panel, as tone-tagged phrases.
export const getStatusParts = (name = "", counts = {}) => {
  const c = counts || {};
  const parts = [];
  if (name === "signals" && n(c.signalsUnread)) parts.push({ text: `${c.signalsUnread} new`, tone: "blue" });
  if (name === "inbox" && n(c.inboxUnread)) parts.push({ text: `${c.inboxUnread} unread`, tone: "red" });
  if (name === "tasks") {
    if (n(c.tasksToday)) parts.push({ text: `${c.tasksToday} today`, tone: "" });
    if (n(c.tasksLate)) parts.push({ text: `${c.tasksLate} late`, tone: "red" });
  }
  if (name === "agents") {
    // One number, the agents page's "Needs you" (it already counts stopped agents); red when one has stopped.
    if (n(c.agentsNeedYou)) parts.push({ text: `${c.agentsNeedYou} ${c.agentsNeedYou === 1 ? "needs" : "need"} you`, tone: n(c.agentsStopped) ? "red" : "" });
  }
  return parts;
};

/*
 * Signals "new since last visit": the unread count seen when Signals was last opened is the
 * watermark. More unread than that = something new. Reading signals lowers the watermark with it.
 */
export const nextSignalsSeen = (unread = 0, seen = 0, onSignalsPage = false) =>
  onSignalsPage || unread < seen ? unread : seen;
export const signalsNewCount = (unread = 0, seen = 0) => Math.max(0, n(unread) - n(seen));

// Storage can throw (private mode, blocked site data); the panel must still work.
export const safeGet = key => {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};
export const safeSet = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    /* not persisted, fine */
  }
};

export const PIN_KEY = "sgSideNavPinned";
export const SIGNALS_SEEN_KEY = "sgSideNavSignalsSeen";
export const readPinned = () => safeGet(PIN_KEY) === "true";
export const writePinned = pinned => safeSet(PIN_KEY, pinned ? "true" : "false");
export const readSignalsSeen = () => +safeGet(SIGNALS_SEEN_KEY) || 0;
export const writeSignalsSeen = v => safeSet(SIGNALS_SEEN_KEY, String(v || 0));

// Long enough that sweeping past the rail doesn't open it, short enough to feel instant.
export const OPEN_DELAY_MS = 120;
// The More menu opens on hover too, and waits a moment before closing so the cursor can cross to it.
export const MORE_OPEN_DELAY_MS = 80;
export const MORE_CLOSE_DELAY_MS = 220;

// Hover intent: open after a short dwell, close at once on leave; a leave before the dwell cancels it.
export const createHoverIntent = (setOpen, delay = OPEN_DELAY_MS) => {
  let timer = null;
  const cancel = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };
  return {
    enter() {
      cancel();
      timer = setTimeout(() => {
        timer = null;
        setOpen(true);
      }, delay);
    },
    leave() {
      cancel();
      setOpen(false);
    },
    now() {
      cancel();
      setOpen(true);
    },
    cancel
  };
};

export const isPanelOpen = (pinned, open) => !!(pinned || open);

// A popover removed from under the cursor (Escape, a click) never fires mouseleave on the nav, so the next
// pointer move decides: outside the nav means the hover is over. Returns a cancel.
export const leaveOnNextMove = (doc, getContainer, onLeave) => {
  const onMove = e => {
    doc.removeEventListener("mousemove", onMove);
    const box = getContainer();
    if (box && !box.contains(e.target)) onLeave();
  };
  doc.addEventListener("mousemove", onMove);
  return () => doc.removeEventListener("mousemove", onMove);
};

// Where the More menu's top sits inside the nav: level with the More row, pulled up when the
// space below is short, never above the first row under the app bar.
export const MORE_MENU_MIN_TOP = 58;
export const MORE_MENU_GAP = 16;
export const moreMenuTopFor = (rowTop, menuHeight, navHeight) =>
  Math.max(MORE_MENU_MIN_TOP, Math.min(rowTop, navHeight - menuHeight - MORE_MENU_GAP));
export const moreMenuTop = (row, nav, menu) =>
  moreMenuTopFor(row.getBoundingClientRect().top - nav.getBoundingClientRect().top, menu.offsetHeight, nav.clientHeight);
